/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { INSPECTOR_NODE_TYPE, isInspectorNodeDefault, TInspectorNode } from "../InspectorNodeTypes";
import {
	ISchemaOneOfModel,
	isSchemaOneOfModel,
	SCHEMA_VALUE_TYPE,
	TGenericModelNode,
	TSchemaOneOfTypes
} from "@adapptio/lib-blueprint";
import { mapModelToNodes } from "../index";
import { getParentComponentName, isEmptyNode } from "../utils";
import { mapperDescriptor } from "../../modules/Core/ReferenceGuide/mappers/mapperDescriptor";
import { mapperExamples } from "../../modules/Core/ReferenceGuide/mappers/mapperExamples";

export function oneOfMapper(model: ISchemaOneOfModel<TSchemaOneOfTypes>): TInspectorNode {
	if (!isSchemaOneOfModel(model)) {
		return;
	}

	const children: Array<() => TInspectorNode> = [];
	const deps: TGenericModelNode[] = [
		model,
		model.type,
		model.type.constant,
		...mapModelToNodes(model.type).deps
	];

	const allTypesKeys = Object.keys(model.schema.opts.types);

	const typeModel = model.type;
	const typeConstModel = model.type.constant;

	const constantType = typeModel.type === SCHEMA_VALUE_TYPE.CONST;

	const typeNode = mapModelToNodes(model.type);

	const typeControls = isInspectorNodeDefault(typeNode) ? typeNode.controls : [];

	const child = mapModelToNodes(model.value[typeConstModel.value]);

	if (child) {
		deps.push(...child.deps);
	}

	if (constantType) {
		const constNode = mapModelToNodes(model.value[typeConstModel.value]);
		if (constNode) {
			if (isInspectorNodeDefault(constNode)) {
				if (constNode.children.length > 0 || constNode.controls.length > 0) {
					children.push(() => constNode);
				}
			} else {
				children.push(() => mapModelToNodes(model.value[typeConstModel.value]));
			}
		}
	} else {
		for (let i = 0; i < allTypesKeys.length; i++) {
			const typeKey = allTypesKeys[i];
			const typeModel = model.value[typeKey];
			const typeNode = mapModelToNodes(typeModel);

			if (isEmptyNode(typeNode)) {
				continue;
			}

			children.push(() => {
				return {
					type: INSPECTOR_NODE_TYPE.DEFAULT,
					key: `${typeModel.nodeId}`,
					label: model.schema.opts.types[typeKey]?.label || typeKey,
					required: model.schema.opts.constraints?.required || false,
					referenceData: getParentComponentName(model),
					descriptor: mapperDescriptor(model.schema),
					examples: mapperExamples(model.schema),
					tooltip: "",
					deps: [ ...typeNode.deps ],
					controls: [],
					children: [ () => typeNode ]
				};
			});
		}
	}

	return {
		type: INSPECTOR_NODE_TYPE.DEFAULT,
		key: `${model.nodeId}`,
		label: model.schema.opts.label,
		tooltip: model.schema.opts.description,
		default: model.schema.opts["default"] || null,
		required: model.schema.opts.constraints?.required || false,
		options: model.schema.opts["options"],
		referenceData: getParentComponentName(model),
		descriptor: mapperDescriptor(model.schema),
		examples: mapperExamples(model.schema),
		deps: deps,
		controls: [ ...typeControls ],
		children: children
	};
}
